import { userClient } from '../config/api';
import {
  ApiResponse,
  Role,
  CreateRoleRequest,
  UpdateRoleRequest,
  GetAllRolesParams,
  AssignPermissionsToRoleRequest,
} from './types/user.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class RoleService {
  private readonly basePath = '/api/roles';

  createRole = async (data: CreateRoleRequest): Promise<Role> => {
    try {
      const response = await userClient.post<ApiResponse<Role>>(this.basePath, data);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error creating role');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error creating role');
    }
  }

  getAllRoles = async (params?: GetAllRolesParams): Promise<Role[]> => {
    try {
      const response = await userClient.get<ApiResponse<Role[]>>(this.basePath, {
        params,
      });

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error fetching roles');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching roles');
    }
  }

  getRoleById = async (id: string): Promise<Role> => {
    try {
      const response = await userClient.get<ApiResponse<Role>>(`${this.basePath}/${id}`);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error fetching role');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching role');
    }
  }

  getRolesByModule = async (moduleType: number): Promise<Role[]> => {
    try {
      const response = await userClient.get<ApiResponse<Role[]>>(
        `${this.basePath}/module/${moduleType}`
      );

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error fetching roles by module');
      }

      return response.data.data || [];
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error fetching roles by module');
    }
  }

  updateRole = async (id: string, data: UpdateRoleRequest): Promise<Role> => {
    try {
      const response = await userClient.put<ApiResponse<Role>>(
        `${this.basePath}/${id}`,
        data
      );

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Error updating role');
      }

      return response.data.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error updating role');
    }
  }

  deleteRole = async (id: string): Promise<void> => {
    try {
      const response = await userClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error deleting role');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error deleting role');
    }
  }

  assignPermissionsToRole = async (
    roleId: string,
    data: AssignPermissionsToRoleRequest
  ): Promise<void> => {
    try {
      const response = await userClient.post<ApiResponse<void>>(
        `${this.basePath}/${roleId}/permissions`,
        data
      );

      if (response.data && !response.data.success) {
        throw new Error(response.data?.errorMessage || 'Error assigning permissions to role');
      }
    } catch (error: unknown) {
      apiErrorHandler(error, 'Error assigning permissions to role');
    }
  }
}

export const roleService = new RoleService();
